import { Box, Typography, Button } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { MailIconSharp } from '../CustomSVGIcons/MailIconSharp';
import { OTPOption } from '../EsOtpOptions/OTPOption';
import { EsFormWrapper } from './EsFormWrapper';
import { MessageIconSharp } from '../CustomSVGIcons/MessageIconSharp';
import { WhatsappIconOutline } from '../CustomSVGIcons/WhatsappIconOutline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { audme_app_icon } from '../../asset/images';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/root-reducer';
import { GenericModal } from '../GenericModal/GenericModal';
import { AudmeQRCodeStep } from './AudmeQRCodeStep';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { strings } from '../../constants/strings';
import {
    buildOtpChannelIcon,
    OtpPreferenceSecurityLayout,
    type OtpChannel,
    type OtpPreferenceOption,
} from './OtpPreferenceSecurityLayout';
import { OBSIDIAN, OBSIDIAN_BODY_FONT } from '../../constants/obsidianDashboardTheme';

interface IEsEnterOtp {
    title?: string;
    subtitle?: string;
    sx?: React.CSSProperties;
    phoneNumber: string;
    email: string;
    onSetOTPPreference: (val: OtpChannel) => void;
    audmeData?: {
        token?: string;
        qrCode?: string;
        deeplink?: string;
    } | null;
    onBack?: () => void;
    loading?: boolean;
    /** Obsidian security layout (AudMe-forward flows). */
    variant?: 'legacy' | 'security';
    stepIndex?: number;
    totalSteps?: number;
}

const maskEmail = (email: string): string => {
    const [name, domain] = email.split('@');
    if (!name || !domain) return email;
    const visible = name.slice(0, 2);
    const masked = '*'.repeat(Math.max(name.length - 2, 8));
    return `${visible}${masked}@${domain}`;
};

const maskPhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7) return phone;
    const visibleStart = digits.slice(0, 4);
    const visibleEnd = digits.slice(-3);
    const maskedMiddle = '*'.repeat(Math.max(digits.length - 7, 4));
    return `+${visibleStart}${maskedMiddle}${visibleEnd}`;
};

const DEFAULT_TWO_FA_CONFIG = {
    audme: true,
    email: true,
    sms: false,
    whatsapp: false,
} as const;

const OTPPage: React.FC<IEsEnterOtp> = ({
    title,
    subtitle,
    sx,
    phoneNumber,
    email,
    onSetOTPPreference,
    audmeData,
    onBack,
    loading,
    variant,
    stepIndex = 1,
    totalSteps = 4,
}) => {
    const { themeStyle } = useThemeContext();
    const useSecurityLayout = variant !== 'legacy';
    const maskedPhone = maskPhone(phoneNumber);
    const maskedEmail = maskEmail(email);
    const [selectedOTP, setSelectedOTP] = useState<OtpChannel | ''>('');
    const [showAudmeSetupModal, setShowAudmeSetupModal] = useState(false);

    const globalConfiguration = useSelector((state: RootState) => state.configuration.globalConfiguration);
    const twoFaConfig = useMemo(
        () => globalConfiguration?.two_fa_configuration ?? DEFAULT_TWO_FA_CONFIG,
        [globalConfiguration?.two_fa_configuration],
    );

    const handleSetOTP = (val: OtpChannel) => {
        setSelectedOTP(val);
        onSetOTPPreference(val);
    };

    const defaultChannel = useMemo((): OtpChannel | null => {
        if (twoFaConfig.email) return 'email';
        if (twoFaConfig.audme) return 'audme';
        if (twoFaConfig.sms) return 'sms';
        if (twoFaConfig.whatsapp) return 'whatsapp';
        return null;
    }, [twoFaConfig]);

    useEffect(() => {
        if (!defaultChannel || selectedOTP) return;
        handleSetOTP(defaultChannel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultChannel]);

    const handleAudmeSetupClick = useCallback(() => {
        if (audmeData?.qrCode && audmeData?.deeplink) {
            setShowAudmeSetupModal(true);
        }
    }, [audmeData?.qrCode, audmeData?.deeplink]);

    const securityOptions = useMemo((): OtpPreferenceOption[] => {
        const opts: OtpPreferenceOption[] = [];

        // if (twoFaConfig.audme) {
        //     opts.push({
        //         channel: 'audme',
        //         viaLabel: 'via audme app',
        //         title: 'Push Authentication',
        //         icon: buildOtpChannelIcon('audme', selectedOTP === 'audme'),
        //         audmeSetupHint:
        //             selectedOTP === 'audme' && audmeData?.qrCode && audmeData?.deeplink && !loading ? (
        //                 <Typography
        //                     component="button"
        //                     type="button"
        //                     onClick={handleAudmeSetupClick}
        //                     sx={{
        //                         fontSize: 12,
        //                         color: OBSIDIAN.primary,
        //                         fontFamily: FONT_ENUM.MAIN_REGULAR,
        //                         cursor: 'pointer',
        //                         textDecoration: 'underline',
        //                         border: 'none',
        //                         bgcolor: 'transparent',
        //                         p: 0,
        //                         textAlign: 'left',
        //                     }}>
        //                     Haven&apos;t set up the audme app? Set up now.
        //                 </Typography>
        //             ) : undefined,
        //     });
        // }

        if (twoFaConfig.email) {
            opts.push({
                channel: 'email',
                viaLabel: 'via email',
                title: maskedEmail,
                icon: buildOtpChannelIcon('email', selectedOTP === 'email'),
            });
        }

        if (twoFaConfig.sms) {
            opts.push({
                channel: 'sms',
                viaLabel: 'via sms',
                title: maskedPhone,
                icon: buildOtpChannelIcon('sms', selectedOTP === 'sms'),
            });
        }

        if (twoFaConfig.whatsapp) {
            opts.push({
                channel: 'whatsapp',
                viaLabel: 'via whatsapp',
                title: maskedPhone,
                icon: buildOtpChannelIcon('whatsapp', selectedOTP === 'whatsapp'),
            });
        }

        return opts;
    }, [twoFaConfig, maskedEmail, maskedPhone, selectedOTP]);

    if (useSecurityLayout) {
        return (
            <Box sx={{ width: '100%', ...sx }}>
                <OtpPreferenceSecurityLayout
                    title={title || 'Select OTP Preference'}
                    subtitle={subtitle || 'Choose where you would like to receive your security code.'}
                    stepIndex={stepIndex}
                    totalSteps={totalSteps}
                    options={securityOptions}
                    selected={selectedOTP}
                    onSelect={handleSetOTP}
                />
                {onBack && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Button
                            onClick={onBack}
                            type="button"
                            startIcon={
                                <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                                    ←
                                </Box>
                            }
                            sx={{
                                textTransform: 'none',
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 14,
                                fontWeight: 500,
                                color: OBSIDIAN.onSurfaceVariant,
                                '&:hover': { color: OBSIDIAN.primary, bgcolor: 'transparent' },
                            }}>
                            {loading ? 'Please wait...' : strings.go_back}
                        </Button>
                    </Box>
                )}
                {showAudmeSetupModal && audmeData?.qrCode && audmeData?.deeplink && (
                    <GenericModal
                        open={showAudmeSetupModal}
                        handleClose={() => setShowAudmeSetupModal(false)}
                        paperStyle={{
                            minHeight: 'fit-content',
                            width: 'fit-content',
                            maxWidth: '500px',
                            height: 'fit-content',
                            padding: 20,
                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                            border: 'transparent',
                        }}>
                        <AudmeQRCodeStep
                            qrCode={audmeData.qrCode}
                            deeplink={audmeData.deeplink}
                            onReady={() => setShowAudmeSetupModal(false)}
                            onBack={() => setShowAudmeSetupModal(false)}
                        />
                    </GenericModal>
                )}
            </Box>
        );
    }

    return (
        <EsFormWrapper title={title || ''} text={subtitle || ''}>
            <Box sx={{ my: 3, width: '100%', ...sx }}>
                {twoFaConfig.audme && (
                    <Box sx={{ width: '100%' }}>
                        <OTPOption
                            type="audme app"
                            info="Open audme app to authenicate"
                            selected={selectedOTP === 'audme'}
                            disabled={!twoFaConfig.audme}
                            onClick={() => handleSetOTP('audme')}
                            startIcon={
                                <Box
                                    sx={{
                                        backgroundColor: theme[themeStyle].grey,
                                        padding: 0.5,
                                        borderRadius: 50,
                                    }}>
                                    <img
                                        src={audme_app_icon}
                                        style={{ height: '25px', width: '25px' }}
                                        alt="audme app icon"
                                    />
                                </Box>
                            }
                        />
                        {selectedOTP === 'audme' && audmeData?.qrCode && audmeData?.deeplink && !loading && (
                            <Typography
                                onClick={handleAudmeSetupClick}
                                sx={{
                                    fontSize: 12,
                                    color: theme[themeStyle].primary_orange,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    cursor: 'pointer',
                                    mt: 1,
                                    ml: 4,
                                    textDecoration: 'underline',
                                }}>
                                Haven&apos;t set up audme app? set up now.
                            </Typography>
                        )}
                    </Box>
                )}
                {twoFaConfig.email && (
                    <OTPOption
                        type="email"
                        info={maskedEmail}
                        onClick={() => handleSetOTP('email')}
                        selected={selectedOTP === 'email'}
                        disabled={!twoFaConfig.email}
                        startIcon={
                            <MailIconSharp
                                style={{
                                    width: '1.8em',
                                    height: '1.8em',
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].grey,
                                    padding: 5,
                                    borderRadius: 50,
                                }}
                            />
                        }
                    />
                )}
                {twoFaConfig.sms && (
                    <OTPOption
                        type="sms"
                        info={maskedPhone}
                        selected={selectedOTP === 'sms'}
                        onClick={() => handleSetOTP('sms')}
                        disabled={!twoFaConfig.sms}
                        startIcon={
                            <MessageIconSharp
                                style={{
                                    width: '1.8em',
                                    height: '1.8em',
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].grey,
                                    padding: 5,
                                    borderRadius: 50,
                                }}
                            />
                        }
                    />
                )}
                {twoFaConfig.whatsapp && (
                    <OTPOption
                        type="whatsapp"
                        info={maskedPhone}
                        disabled={!twoFaConfig.whatsapp}
                        selected={selectedOTP === 'whatsapp'}
                        onClick={() => handleSetOTP('whatsapp')}
                        startIcon={
                            <WhatsappIconOutline
                                style={{
                                    width: '1.8em',
                                    height: '1.8em',
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].grey,
                                    padding: 5,
                                    borderRadius: 50,
                                }}
                            />
                        }
                    />
                )}
            </Box>
            {onBack && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        onClick={onBack}
                        type="button"
                        sx={{
                            width: 'fit-content',
                            height: 'fit-content',
                            paddingLeft: 2,
                            paddingRight: 2,
                            paddingTop: 1,
                            paddingBottom: 1,
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                        }}>
                        <Typography
                            sx={{
                                textAlign: 'center',
                                fontSize: 12,
                                fontStyle: 'normal',
                                fontWeight: '400',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].lightWhite,
                            }}>
                            {loading ? 'Please wait...' : strings.go_back}
                        </Typography>
                    </Button>
                </Box>
            )}
            {showAudmeSetupModal && audmeData?.qrCode && audmeData?.deeplink && (
                <GenericModal
                    open={showAudmeSetupModal}
                    handleClose={() => setShowAudmeSetupModal(false)}
                    paperStyle={{
                        minHeight: 'fit-content',
                        width: 'fit-content',
                        maxWidth: '500px',
                        height: 'fit-content',
                        padding: 20,
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        border: 'transparent',
                    }}>
                    <AudmeQRCodeStep
                        qrCode={audmeData.qrCode}
                        deeplink={audmeData.deeplink}
                        onReady={() => setShowAudmeSetupModal(false)}
                        onBack={() => setShowAudmeSetupModal(false)}
                    />
                </GenericModal>
            )}
        </EsFormWrapper>
    );
};

export { OTPPage };
